import { useCallback } from "react";
import {
  useMutation,
  type MutateOptions,
  type UseMutationOptions,
  type UseMutationResult,
} from "@tanstack/react-query";

/** One request with the parent (project, draft, budget) it was started for. */
export type ScopedRequest<TVariables, TScope> = { variables: TVariables; scope: TScope };

/** The caller's own callbacks, handed the variables it passed rather than the scoped request. */
function forCaller<TData, TError, TVariables, TContext, TScope>(
  variables: TVariables,
  supplied: MutateOptions<TData, TError, TVariables, TContext> | undefined
): MutateOptions<TData, TError, ScopedRequest<TVariables, TScope>, TContext> | undefined {
  if (!supplied) return undefined;
  const { onSuccess, onError, onSettled } = supplied;
  return {
    onSuccess: onSuccess && ((data, _request, context) => onSuccess(data, variables, context)),
    onError: onError && ((error, _request, context) => onError(error, variables, context)),
    onSettled:
      onSettled && ((data, error, _request, context) => onSettled(data, error, variables, context)),
  };
}

/**
 * `useMutation` whose `mutate` and `mutateAsync` send the scope of the render that calls them
 * along with the variables. React Query hands a request each new render's options until it
 * settles, including a request still waiting to be sent while offline, so a callback that read
 * the open project or draft from the render would act on whatever is open when it runs. Every
 * callback here reads the parent from `request.scope` instead.
 *
 * Pass a memoized `scope` so `mutate` and `mutateAsync` stay stable while the parent does.
 */
export function useScopedMutation<TData, TError, TVariables, TContext, TScope>(
  scope: TScope,
  options: UseMutationOptions<TData, TError, ScopedRequest<TVariables, TScope>, TContext>
): UseMutationResult<TData, TError, TVariables, TContext> {
  const mutation = useMutation(options);
  const { mutate: send, mutateAsync: sendAsync } = mutation;

  const mutate = useCallback(
    (variables: TVariables, supplied?: MutateOptions<TData, TError, TVariables, TContext>) =>
      send({ variables, scope }, forCaller(variables, supplied)),
    [scope, send]
  );
  const mutateAsync = useCallback(
    (variables: TVariables, supplied?: MutateOptions<TData, TError, TVariables, TContext>) =>
      sendAsync({ variables, scope }, forCaller(variables, supplied)),
    [scope, sendAsync]
  );

  return { ...mutation, variables: mutation.variables?.variables, mutate, mutateAsync };
}
